import type { CreateStartupPayload } from './api/startups.api';
import type { CreateStartupFormValues } from './schema';

/** Drop blank strings so the backend receives absent optionals, not empties. */
function clean(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

type Payload = CreateStartupPayload;

/**
 * name/title are optional at the form-values layer (2026-08-05 relaxation,
 * see schema.ts) - a founding-team row is only kept once it has a name,
 * guarding both the filter and the trim() below against `undefined`.
 */
function toCreateTeam(values: CreateStartupFormValues): Payload['foundingTeam'] {
  const team = (values.foundingTeam ?? [])
    .filter((member) => Boolean(member.name?.trim()))
    .map((member) => ({
      name: (member.name ?? '').trim(),
      title: (member.title ?? '').trim(),
      linkedinUrl: clean(member.linkedinUrl),
      pastExits: member.pastExits === 'yes',
      // No `photoUrl` (dropped, MYN-175) and no `bio` (removed from the
      // product, client UX sheet r127): the create DTO rejects unknown keys.
    }));
  return team.length ? team : undefined;
}

/** Revenue, traction and the current raise. */
function toCreateFinancials(values: CreateStartupFormValues): Partial<Payload> {
  const hasLeadInvestor = values.hasLeadInvestor === 'yes';
  return {
    totalRevenueRange: clean(values.totalRevenueRange),
    arr: values.arr,
    amountRaisedToDate: values.amountRaisedToDate,
    nondilutiveFunding: values.nondilutiveFunding,
    revenueModel: clean(values.revenueModel),
    revenueGrowthRate: clean(values.revenueGrowthRate),
    payingCustomers: clean(values.payingCustomers),
    investorTypesSought: values.investorTypesSought?.length
      ? values.investorTypesSought
      : undefined,
    useOfFunds: clean(values.useOfFunds),
    financeSoughtLow: values.financeSoughtLow,
    financeSoughtHigh: values.financeSoughtHigh,
    valuationOrCap: values.valuationOrCap,
    instrument: clean(values.instrument),
    targetCloseDate: clean(values.targetCloseDate),
    // An explicit Yes/No (UX sheet r151); unanswered is sent as "no". The name
    // only travels with a "yes" - a name typed before switching to "no" is dropped.
    hasLeadInvestor,
    leadInvestorName: hasLeadInvestor ? clean(values.leadInvestorName) : undefined,
  };
}

/** Unanswered stays absent - never defaulted to "no" on the founder's behalf. */
function toCreateSelfId(values: CreateStartupFormValues): Partial<Payload> {
  return {
    founderFemale: values.founderFemale,
    founderUnderrepresented: values.founderUnderrepresented,
    founderUnderrepresentedCategories:
      values.founderUnderrepresented === 'yes' && values.founderUnderrepresentedCategories?.length
        ? values.founderUnderrepresentedCategories
        : undefined,
  };
}

/**
 * Map validated form values to the create-startup API payload: strips blank
 * optionals, drops empty collections, and keeps only founding-team members
 * that have a name so the backend stores a clean partial profile.
 * `workEmail` is the signed-in user's account email (user decision 2026-07-16:
 * the account email IS the work email), used by the Backend's
 * `assertWorkEmail`/`checkEligibility` domain check - no OTP is involved.
 */
export function toCreatePayload(values: CreateStartupFormValues, workEmail: string): Payload {
  return {
    companyName: values.companyName.trim(),
    workEmail: workEmail.trim(),
    briefDescription: clean(values.briefDescription),
    companyWebsiteUrl: clean(values.companyWebsiteUrl),
    companyLinkedinUrl: clean(values.companyLinkedinUrl),
    sectorId: values.sectorId,
    subsectorIds: values.subsectorIds?.length ? values.subsectorIds : undefined,
    // Only ever set while "Other" is the sector - SectorStage clears it when
    // the sector changes away - so a blank here means "not Other".
    sectorOther: clean(values.sectorOther),
    fundingStage: clean(values.fundingStage),
    yearFounded: values.yearFounded,
    hqCountryId: values.hqCountryId,
    // Absent for a non-US city: the Backend derives the state from the city.
    hqStateId: values.hqStateId,
    hqCityId: values.hqCityId,
    marketModels: values.marketModels?.length ? values.marketModels : undefined,
    // The write field is `logoFileKey` (MYN-175); `logoUrl` is read-only and
    // is deliberately never sent - the Backend rejects it on create.
    logoFileKey: clean(values.logoFileKey),
    pitchDeckFileKey: clean(values.pitchDeckFileKey),
    pitchDeckFileSizeBytes: values.pitchDeckFileSizeBytes,
    pitchVideoUrl: clean(values.pitchVideoUrl),
    recentMilestone: clean(values.recentMilestone),
    aiClassification: clean(values.aiClassification),
    employeeCount: values.employeeCount,
    ...toCreateFinancials(values),
    ...toCreateSelfId(values),
    foundingTeam: toCreateTeam(values),
  };
}
